import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

interface Challenge {
  id: string;
  title: string;
  description: string;
  theme: string;
  start_date: string;
  end_date: string;
  prize_points: number;
  is_active: boolean;
  entries_count?: number;
}

interface ChallengeEntry {
  id: string;
  challenge_id: string;
  user_id: string;
  outfit_data: {
    items: Array<{
      id: string;
      name: string;
      image_url: string | null;
      type: string;
    }>;
  };
  caption: string | null;
  votes_count: number;
  created_at: string;
  user_profile?: {
    display_name: string | null;
    avatar_url: string | null;
    aura_level: string | null;
  };
  has_voted?: boolean;
}

export function useChallenges() {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [entries, setEntries] = useState<ChallengeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<ChallengeEntry[]>([]);

  const fetchChallenges = useCallback(async () => {
    const { data } = await supabase
      .from('challenges')
      .select('*')
      .eq('is_active', true)
      .order('end_date', { ascending: true });

    if (data) {
      const enriched = await Promise.all(data.map(async (challenge) => {
        const { count } = await supabase
          .from('challenge_entries')
          .select('*', { count: 'exact', head: true })
          .eq('challenge_id', challenge.id);
        
        return { ...challenge, entries_count: count || 0 };
      }));
      setChallenges(enriched);
    }
    setLoading(false);
  }, []);

  const fetchEntries = useCallback(async (challengeId: string) => {
    const { data } = await supabase
      .from('challenge_entries')
      .select('*')
      .eq('challenge_id', challengeId)
      .order('votes_count', { ascending: false });

    if (data) {
      const enriched: ChallengeEntry[] = await Promise.all(data.map(async (entry) => {
        // Use public_profiles view for viewing other users' profiles
        const { data: profile } = await supabase
          .from('public_profiles' as any)
          .select('display_name, avatar_url, aura_level')
          .eq('id', entry.user_id)
          .single() as { data: { display_name: string | null; avatar_url: string | null; aura_level: string | null } | null };

        let hasVoted = false;
        if (user) {
          const { data: vote } = await supabase
            .from('challenge_votes')
            .select('id')
            .eq('entry_id', entry.id)
            .eq('user_id', user.id)
            .single();
          hasVoted = !!vote;
        }

        return {
          ...entry,
          outfit_data: entry.outfit_data as ChallengeEntry['outfit_data'],
          user_profile: profile || undefined,
          has_voted: hasVoted
        };
      }));
      
      setEntries(enriched);
      setLeaderboard(enriched.slice(0, 10));
    }
  }, [user]);

  const submitEntry = async (
    challengeId: string, 
    outfitData: ChallengeEntry['outfit_data'],
    caption?: string
  ) => {
    if (!user) {
      toast({ title: "Please sign in", description: "You need to be signed in to enter challenges" });
      return false;
    }

    const { error } = await supabase
      .from('challenge_entries')
      .insert({
        challenge_id: challengeId,
        user_id: user.id,
        outfit_data: outfitData,
        caption
      });

    if (error) {
      if (error.code === '23505') {
        toast({ title: "Already entered", description: "You've already submitted an entry for this challenge" });
      } else {
        toast({ title: "Error", description: "Failed to submit entry" });
      }
      return false;
    }

    toast({ title: "Entry submitted!", description: "Good luck in the challenge!" });
    await fetchEntries(challengeId);
    return true;
  };

  const voteForEntry = async (entryId: string, challengeId: string) => {
    if (!user) {
      toast({ title: "Please sign in", description: "You need to be signed in to vote" });
      return;
    }

    const entry = entries.find(e => e.id === entryId);
    
    if (entry?.has_voted) {
      // Remove vote
      await supabase
        .from('challenge_votes')
        .delete()
        .eq('entry_id', entryId)
        .eq('user_id', user.id);
    } else {
      // Add vote
      const { error } = await supabase
        .from('challenge_votes')
        .insert({
          entry_id: entryId,
          user_id: user.id
        });

      if (error) {
        toast({ title: "Error", description: "Failed to vote" });
        return;
      }
    }

    await fetchEntries(challengeId);
  };

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return {
    challenges,
    entries,
    leaderboard,
    loading,
    fetchEntries,
    submitEntry,
    voteForEntry,
    refetch: fetchChallenges
  };
}
